<template>
  <div class="with-rapid-form">
    <div class="files">
      <div class="card files">
        <div class="card-header">
          <span class="align-middle">Attachments</span>
          <label
            v-if="canUpload && !isUploading"
            class="btn btn-sm btn-primary float-end"
          >
            <span class="glyphicons icon-left upload" /><span>Add new file</span>
            <input
              ref="uploadField"
              type="file"
              style="display: none"
              @change="uploadFile"
            >
          </label>
        </div>
        <div
          v-if="hasFiles"
          class="list-group list-group-flush"
        >
          <div
            v-for="file in files"
            :key="file.id"
            :class="{ 'list-group-item': true, 'list-group-item-danger': file.rm }"
          >
            <div class="row gx-2">
              <input
                :name="`${fieldName}[attachments_attributes][][id]`"
                :value="file.id"
                type="hidden"
              >
              <input
                type="hidden"
                :name="`${fieldName}[attachments_attributes][][_destroy]`"
                :value="file.rm ? 1 : 0"
              >
              <div class="col mb-3">
                <label class="form-label">File (size: {{ file.size_str }})</label>
                <input
                  type="text"
                  readonly
                  class="form-control form-control-sm"
                  :value="file.filename"
                >
              </div>
              <div class="col col-auto form-group-button">
                <button
                  v-if="!file.rm"
                  type="button"
                  class="btn btn-sm btn-danger"
                  @click="removeFile(file)"
                >
                  Delete
                </button>
                <button
                  v-if="file.rm"
                  type="button"
                  class="btn btn-sm btn-info"
                  @click="restoreFile(file)"
                >
                  Restore
                </button>
              </div>
            </div>
            <div
              v-if="file.rm"
              class="text-danger fst-italic"
            >
              File will be deleted on save
            </div>
          </div>
        </div>
        <div
          v-if="noFiles"
          class="card-body"
        >
          No files uploaded yet
        </div>
      </div>
    </div>

    <alert
      type="danger"
      :message="uploadError"
      @dismiss="clearError"
    />
    <loading-indicator
      v-if="isUploading"
      :message="progressMessage"
      additional-css-class="mt-4 mb-2"
    />
    <div
      v-if="isUploading"
      class="upload-progress"
    >
      <div class="progress">
        <div
          class="progress-bar"
          role="progressbar"
          :style="uploadProgressStyle"
          :aria-valuenow="uploadProgress"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          {{ uploadProgress }}%
        </div>
      </div>
      <div class="status-label text-secondary">
        {{ progressMessage }}
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {computed, onMounted, ref, useTemplateRef, type CSSProperties, type PropType} from "vue"
import axios from "axios"
import Rails from "@rails/ujs"

import LoadingIndicator from "./loading-indicator.vue"
import Alert from "./alert.vue"

import { isNonEmptyString } from "../js-utils"

interface UploadFile {
  id: string;
  rm: boolean;
  size_str: string;
  filename: string;
}

const props = defineProps({
  fieldName: { type: String, required: true },
  uploadUrl: { default: null, type: String },
  initialFiles: { default: null, type: Array as PropType<UploadFile[] | null> },
})

const uploadField = useTemplateRef<HTMLInputElement>('uploadField')

const isUploading = ref(false)
const uploadProgress = ref(0)
const uploadError = ref<string | undefined>()
const progressMessage = ref<string | null>(null)
const files = ref<UploadFile[]>([])

const canUpload = computed((): boolean => {
  return isNonEmptyString(props.uploadUrl)
})

const hasFiles = computed((): boolean => {
  return files.value.length > 0
})

const noFiles = computed((): boolean => {
  return files.value.length === 0
})

const uploadProgressStyle = computed((): CSSProperties => {
  return {
    width: `${uploadProgress.value}%`,
    minWidth: '3rem'
  }
})

onMounted(() => {
  if (Array.isArray(props.initialFiles)) {
    const newFiles = [] as UploadFile[]
    for (const fileData of props.initialFiles) {
      const file = JSON.parse(JSON.stringify(fileData)) as UploadFile
      newFiles.push(file)
    }
    files.value = newFiles
  }
})

const uploadFile = (): void => {
  if (!canUpload.value) {
    return
  }

  if (!uploadField.value?.files) {
    return
  }

  const file = uploadField.value.files[0]
  if (file == null) {
    // IE 11 will fire change event when setting uploadField.value to ''
    // if file is null, then do not anything
    return
  }

  const formData = new FormData()
  formData.append('file', file)

  // IE 11 will fire change event again
  uploadField.value.value = ''

  isUploading.value = true
  uploadProgress.value = 0
  progressMessage.value = 'Uploading file ...'
  uploadError.value = undefined

  axios.post(props.uploadUrl, formData, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': Rails.csrfToken()
    },
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total == null) {
        return
      }

      uploadProgress.value = Math.floor(progressEvent.loaded / progressEvent.total * 100)
      if (progressEvent.loaded === progressEvent.total) {
        progressMessage.value = 'Processing file ...'
      }
    }
  })
    .then((response) => {
      if (response.data) {
        if (response.data.file) {
          files.value.push(response.data.file)
        } else if (response.data.error) {
          uploadError.value = response.data.error
        } else {
          uploadError.value = 'Invalid response from server'
        }
      }
      isUploading.value = false
    })
    .catch((error) => {
      uploadError.value = error.message
      isUploading.value = false
    })
}

const clearError = (): void => {
  uploadError.value = undefined
}

const removeFile = (file: UploadFile): void => {
  if (confirm('Are you sure?')) {
    file.rm = true
  }
}

const restoreFile = (file: UploadFile): void => {
  if (confirm('Are you sure?')) {
    file.rm = false
  }
}
</script>
